import { format, parseISO } from "date-fns";
import toast from "react-hot-toast";
import { DATE_FORMATS } from "@/config";
import {
  ERROR_PRIMARY,
  SUCCESS_PRIMARY,
  WARNING_PRIMARY,
} from "@/config/mui/colorPalette";
import { isImage } from "./images";

export const getAspectRatio = (size: { width: number; height: number }) => {
  if (size.height === 0) {
    throw new Error("Height cannot be zero when calculating aspect ratio");
  }

  return (size.width / size.height).toString();
};

export function getOnlineStorePackageStatus(
  startDate: string | null,
  endDate: string | null,
  isCurrentlyInOnlineStore: boolean
): { inOnlineStoreCopy: string; color: string } {
  const currentDate = new Date();

  const formatDate = (date: string) =>
    date ? format(parseISO(date), DATE_FORMATS.DATE_PICKER) : "";

  if (isCurrentlyInOnlineStore) {
    return {
      inOnlineStoreCopy: `Yes${startDate || endDate ? "," : ""} ${startDate ? `since ${formatDate(startDate)}` : ""} ${endDate ? `to ${formatDate(endDate)}` : ""}`,
      color: SUCCESS_PRIMARY,
    };
  }

  const start = startDate ? parseISO(startDate) : null;
  const end = endDate ? parseISO(endDate) : null;

  if (startDate && start > currentDate) {
    return {
      inOnlineStoreCopy: `Not yet, available since ${formatDate(startDate)} ${
        endDate ? `to ${formatDate(endDate)}` : ""
      }`,
      color: WARNING_PRIMARY,
    };
  }

  return {
    inOnlineStoreCopy: `No${endDate && end < currentDate ? `, expired on ${formatDate(endDate)}` : ""}`,
    color: ERROR_PRIMARY,
  };
}

export const validateFiles = async (files: File[]): Promise<File | null> => {
  if (files.length !== 1) {
    toast.error("Please upload exactly one file.");
    return;
  }

  const file = files[0];

  const isImageFile = await isImage(file);

  if (!file || !isImageFile) {
    toast.error(
      "The selected file is invalid. You can only upload image files."
    );
    return;
  }

  if (file.size > MAX_BANNER_SIZE_BYTES) {
    toast.error(`File size exceeds the maximum limit of ${MAX_BANNER_SIZE}MB.`);
    return;
  }

  return file;
};

export enum Banner {
  DESKTOP = "desktop",
  MOBILE = "mobile",
}

export const DESKTOP_BANNER_SIZE = { width: 1128, height: 200 };
export const MOBILE_BANNER_SIZE = { width: 343, height: 192 };

export const DESKTOP_BANNER_AR = getAspectRatio(DESKTOP_BANNER_SIZE);
export const MOBILE_BANNER_AR = getAspectRatio(MOBILE_BANNER_SIZE);

export const MAX_BANNER_SIZE = 3;
export const MAX_BANNER_SIZE_BYTES = MAX_BANNER_SIZE * 1024 * 1024;
